import React, { useState, useEffect } from "react";

import { Image, X, Edit2 } from "lucide-react";
import { useParams } from "react-router";
import {
  FetchAutomationsForCollab,
  LinkMediaForCollab,
} from "../../Api/ApiEndPoints";
import { callNonStoreApiNew } from "../../NonStoreApis";
import useWindowSize from "../../Hooks/useWindowSize";
import { CircularProgress, Drawer } from "@mui/material";
import PostsContent from "./PostsContent";
import DialogWrapper from "../../DialogWrapper";
import useDialogActionsHook from "../../CustomHooks.js/useDialogActionsHook";
import { useSelector } from "react-redux";
import { Button, Card } from "antd";
import InstagramAccountSwitcher from "./InstagramAccountSwitcher";
import { useLayout } from "../../CustomHooks.js/LayoutContext";
import { AddFlex } from "../../reusableStyles";
import { enqueueSnackbar } from "notistack";
import { isNullOrUndefined } from "../../utils";
import { primaryActionColor } from "../../constants";

const MediaSelector = () => {
  const { isMobileView } = useWindowSize();
  let { campaignId, instaUsername } = useParams();
  const { setLayoutComponent, clearLayoutComponent } = useLayout();
  const { credentials } = useSelector((state) => state.instagram);
  const { apiUserData } = useSelector((state) => state.user);
  const [automationStates, setAutomations] = useState([]);

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const response = await callNonStoreApiNew(
          "post",
          FetchAutomationsForCollab.replace(":id", campaignId),
          {
            campaignId,
            instaUserId: credentials.user_id,
          }
        );
        if (response) {
          setAutomations(response.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCampaignData();
  }, []);

  useEffect(() => {
    setLayoutComponent(InstagramAccountSwitcher, {
      username: instaUsername,
      disabled: true,
    });
    return () => {
      clearLayoutComponent();
    };
  }, []);

  return (
    <div style={{ margin: isMobileView() ? "20px" : "20px 48px" }}>
      <AddFlex alignItems="center" className=" mb-6" justify="space-between">
        <h2 className="text-xl md:text-2xl font-semibold">
          Link Media to Automations
        </h2>
      </AddFlex>
      <div className="grid gap-4 md:gap-6 pb-40">
        {automationStates?.map((automation) => (
          <CollaborationAutomationCard
            owner={automation.owner}
            automation={automation}
            apiUserData={apiUserData}
          />
        ))}
      </div>
    </div>
  );
};

export default MediaSelector;

const CollaborationAutomationCard = ({ apiUserData, owner, automation }) => {
  const { isMobileView } = useWindowSize();
  const { campaignId } = useParams();
  const { posts, credentials } = useSelector((state) => state.instagram);
  const [_automation, setAutomation] = useState(automation);

  const [isApiLoading, setIsApiLoading] = useState(false);
  const { isDialogOpen, openDialog, closeDialog } = useDialogActionsHook();

  const handleMediaSelect = (mediaItem) => {
    setAutomation((prev) => ({
      ...prev,
      automations: {
        ...prev.automations,
        mediaData: {
          ...prev.automations.mediaData,
          ...mediaItem,
        },
      },
    }));
    setIsApiLoading(true);
    closeDialog();
  };

  const handleCreateAutomations = async () => {
    try {
      let campaign = {
        owner: owner,
        automations: [_automation],
      };
      const response = await callNonStoreApiNew("post", LinkMediaForCollab, {
        campaign,
        campaignId: campaignId,
        instaUserId: credentials.user_id,
        userId: apiUserData.userId,
        status: isNullOrUndefined(_automation.automations.mediaData.mediaId)
          ? "deleted"
          : "live",
      });
      enqueueSnackbar("Successfully updated Creative", {
        variant: "success",
      });
    } catch (error) {
      console.log(error);
      setAutomation(automation);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
      });
    } finally {
      setIsApiLoading(false);
    }
  };

  useEffect(() => {
    if (isApiLoading) {
      handleCreateAutomations();
    }
  }, [_automation]);
  return (
    <>
      <Card
        key={automation.id}
        style={{
          width: isMobileView() ? "100%" : "70%",
          boxShadow: "0 0 2px 2px rgba(0,0,0,0.2)",
        }}
        className="overflow-hidden hover:border-[#2e5dcd] transition-colors"
      >
        <div className="flex flex-col md:flex-row min-h-[10rem] md:h-40">
          {_automation.automations.mediaData?.mediaUrl ? (
            <>
              <div className="w-full md:w-40 h-40 relative">
                <img
                  src={_automation.automations.mediaData?.mediaUrl}
                  alt="Selected media"
                  className="w-full h-full object-cover"
                  style={{ borderRadius: "10px" }}
                />
              </div>
              <div className="flex-1 p-4 md:p-6 flex flex-col md:flex-row md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium">
                    {_automation.automations.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2">
                    {_automation.automations.mediaData.isStory
                      ? "Story"
                      : "Post/Reel"}
                  </p>
                </div>
                <div className="flex gap-2 md:self-start">
                  <Button
                    variant="solid"
                    size="sm"
                    onClick={() => {
                      openDialog();
                    }}
                    className="flex items-center gap-2"
                    style={{
                      backgroundColor: primaryActionColor,
                      color: "white",
                    }}
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="hidden md:inline">Change</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsApiLoading(true);
                      setAutomation((prev) => ({
                        ...prev,
                        automations: {
                          ...prev.automations,
                          mediaData: {
                            ...prev.automations.mediaData,
                            mediaId: null,
                            mediaUrl: null,
                            mediaType: null,
                          },
                        },
                      }));
                    }}
                    className="flex items-center gap-2 text-red-500 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                    <span className="hidden md:inline">Remove</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-medium">
                  {_automation.automations.name}
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  {_automation.automations?.mediaData?.isStory
                    ? "Story"
                    : "Post/Reel"}
                </p>
              </div>
              <Button
                variant="default"
                onClick={() => {
                  openDialog();
                }}
                style={{
                  backgroundColor: primaryActionColor,
                  color: "white",
                }}
                className="w-full md:w-auto flex items-center justify-center gap-2"
              >
                <Image className="w-4 h-4" />
                Select{" "}
                {_automation.automations?.mediaData?.isStory
                  ? "Story"
                  : "Media"}
              </Button>
            </div>
          )}
        </div>
        {isApiLoading && (
          <AddFlex
            flexDirection="column"
            alignItems="center"
            justify="center"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              backgroundColor: "rgba(0,0,0,0.7)",
              top: 0,
              left: 0,
              fontSize: "18px",
              fontWeight: "600",
            }}
            color="white"
          >
            Please Wait...
            <CircularProgress size={18} sx={{ color: "white" }} />
          </AddFlex>
        )}
      </Card>
      <DialogWrapper
        open={isDialogOpen}
        closeDialog={closeDialog}
        component={
          <PostsContent
            isStory={_automation.automations?.mediaData?.isStory}
            closeDialog={closeDialog}
            posts={posts}
            mediaData={{}}
            onClick={(mediaData) => {
              handleMediaSelect(mediaData);
            }}
          />
        }
      />
    </>
  );
};
